/**
 * DataTable — a thin themed wrapper around a native `<table>`.
 *
 * This is intentionally NOT a data-grid: it owns the surface (rounded border,
 * sticky-friendly header, row hover, consistent cell padding) and leaves the
 * `<thead>/<tbody>` markup to the caller, so existing table-shaped screens can
 * adopt it incrementally. Compose with the sub-parts:
 *
 *   <DataTable>
 *     <DataTable.Head>
 *       <tr><DataTable.Th>Name</DataTable.Th><DataTable.Th>Status</DataTable.Th></tr>
 *     </DataTable.Head>
 *     <DataTable.Body>
 *       <DataTable.Row><DataTable.Td>…</DataTable.Td>…</DataTable.Row>
 *     </DataTable.Body>
 *   </DataTable>
 */
import type {
  HTMLAttributes,
  ReactElement,
  TableHTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react';

import { cn } from './cn.js';

export type DataTableProps = TableHTMLAttributes<HTMLTableElement>;

export function DataTable({
  className,
  children,
  ...props
}: DataTableProps): ReactElement {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-card">
      <div className="overflow-x-auto">
        <table
          className={cn('w-full border-collapse text-sm', className)}
          {...props}
        >
          {children}
        </table>
      </div>
    </div>
  );
}

function Head({
  className,
  ...props
}: HTMLAttributes<HTMLTableSectionElement>): ReactElement {
  return (
    <thead
      className={cn('bg-surface-2 text-muted', className)}
      {...props}
    />
  );
}

function Body({
  className,
  ...props
}: HTMLAttributes<HTMLTableSectionElement>): ReactElement {
  return <tbody className={cn(className)} {...props} />;
}

function Row({
  className,
  ...props
}: HTMLAttributes<HTMLTableRowElement>): ReactElement {
  return (
    <tr
      className={cn(
        'border-t border-border transition-colors first:border-t-0 hover:bg-surface-2/60',
        className,
      )}
      {...props}
    />
  );
}

function Th({
  className,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement>): ReactElement {
  return (
    <th
      scope="col"
      className={cn(
        'px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide',
        className,
      )}
      {...props}
    />
  );
}

function Td({
  className,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement>): ReactElement {
  return (
    <td className={cn('px-3 py-2.5 align-middle text-text', className)} {...props} />
  );
}

DataTable.Head = Head;
DataTable.Body = Body;
DataTable.Row = Row;
DataTable.Th = Th;
DataTable.Td = Td;
